/**
 * Menu System Configuration
 *
 * This file provides configuration for the switchable menu system.
 * The menu can be loaded from the database (dynamic) or from a static configuration.
 *
 * Set MENU_MODE to 'static' to use the hardcoded menu items,
 * or 'dynamic' to load menu items from the database (legacy behavior).
 */

export type MenuMode = 'static' | 'dynamic';

/**
 * Current menu mode setting.
 * Change this to switch between static and dynamic menu loading.
 */
export const MENU_MODE: MenuMode = 'static';

/**
 * Static menu item definition
 */
export interface StaticMenuItem {
    id: number;
    name: string;
    path: string;
    icon?: string;
    parent?: number;
    level: number;
    visible: boolean;
    enabled: boolean;
    /**
     * When true, only an administrator sees the entry. Absent means the user's category decides
     * (User categories, on the Configuration page), which is the case for almost every page here.
     * Enforced by `domain/menuVisibility.ts`.
     */
    adminOnly?: boolean;
    /**
     * The separately sold module the page belongs to. While that module is not available the
     * entry is shown greyed out. Enforced by `domain/menuVisibility.ts`.
     */
    moduleKey?: string;
}

/**
 * Static menu items - all implemented pages in the system
 * This list includes all routes defined in routeConfig.tsx
 */
export const staticMenuItems: StaticMenuItem[] = [
    {
        id: 3,
        name: 'Contracts',
        path: '/contracts',
        icon: 'contracts',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 9,
        name: 'Reports',
        path: '/reports',
        icon: 'reports',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 7,
        name: 'Inventory Movement',
        path: '/inventory-movement',
        icon: 'inventory-movement',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 15,
        name: 'Goods Sale',
        path: '/goods-sale',
        icon: 'goods-sale',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 8,
        name: 'Metals',
        path: '/metals',
        icon: 'metals',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 16,
        name: 'Registru',
        path: '/regiszter',
        icon: 'regiszter',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 22,
        name: 'Dispozitii',
        path: '/dispozitii',
        icon: 'dispozitii',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 14,
        name: 'Listings',
        path: '/frmlistari',
        icon: 'frmlistari',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 24,
        name: 'Change User',
        path: '/schutil',
        icon: 'schutil',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 12,
        name: 'Cash Drawer',
        path: '/pred_casa',
        icon: 'pred_casa',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 20,
        name: 'Contabilitate',
        path: '/contabilitate',
        icon: 'contabilitate',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 18,
        name: 'Rapoarte',
        path: '/rapoarte',
        icon: 'rapoarte',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 17,
        name: 'Verify Last Session',
        path: '/frmverifylastsession',
        icon: 'frmverifylastsession',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 1,
        name: 'Dashboard',
        path: '/dashboard',
        icon: 'dashboard',
        level: 0,
        visible: false,
        enabled: true,
    },
    {
        id: 2,
        name: 'Clients',
        path: '/clients',
        icon: 'clients',
        level: 0,
        visible: false, // Clients menu item hidden - only accessible from contracts page
        enabled: true,
    },
    {
        id: 4,
        name: 'Control Room',
        path: '/control-room',
        icon: 'control-room',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 5,
        name: 'Statistics',
        path: '/statistics',
        icon: 'statistics',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 6,
        name: 'Inventory',
        path: '/inventory',
        icon: 'inventory',
        level: 0,
        visible: false,
        enabled: false,
    },
    {
        id: 10,
        name: 'Settings',
        path: '/settings',
        icon: 'settings',
        level: 0,
        visible: false,
        enabled: true,
    },
    {
        id: 19,
        name: 'Advanced',
        path: '/advanced',
        icon: 'advanced',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 11,
        name: 'Configuration',
        path: '/configuration',
        icon: 'configuration',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        // Administrators only. Every workstation is set up by an administrator, and the release
        // API serves `internal` and `qa` to an administrator alone - everyone else may see
        // `stable`, which cannot exist while builds are unsigned. A cashier therefore reached a
        // page that could only ever be empty for them.
        id: 23,
        name: 'Workstation Service',
        path: '/hardware-service',
        icon: 'hardware-service',
        level: 0,
        visible: true,
        enabled: true,
        adminOnly: true,
    },
    {
        id: 13,
        name: 'Salary',
        path: '/salary',
        icon: 'salary',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        id: 21,
        name: 'Storno',
        path: '/frmstorno',
        icon: 'frmstorno',
        level: 0,
        visible: true,
        enabled: true,
    },
    {
        // A separately sold module: shown to everyone, greyed out until the module is licensed
        // and switched on by an administrator.
        id: 25,
        name: 'E-Invoice',
        path: '/e-invoice',
        icon: 'e-invoice',
        level: 0,
        visible: true,
        enabled: true,
        moduleKey: 'e-invoice',
    },
];

/**
 * Convert static menu items to the format expected by the UI components
 * This mimics the database menu item structure
 */
export interface MenuItemData {
    Id: number;
    Name: string;
    Parent: number;
    Enabled: number;
    Level: number;
    Hasc: number;
    Visible: number;
    Open: number;
    formno: number | null;
    form?: string;
    Button?: string;
}

/**
 * Convert static menu items to database-compatible format.
 *
 * Pass the entries the signed-in user may see (`visibleMenuItems`) to build a menu for that user;
 * the default is every entry that is switched on.
 */
export function staticMenuToDbFormat(
    items: readonly StaticMenuItem[] = staticMenuItems,
): MenuItemData[] {
    return items
        .filter(item => item.visible && item.enabled)
        .map(item => ({
            Id: item.id,
            Name: item.name,
            Parent: item.parent ?? 999,
            Enabled: item.enabled ? 1 : 0,
            Level: item.level,
            Hasc: 0,
            Visible: item.visible ? 1 : 0,
            Open: 0,
            formno: null,
            form: item.path.replace(/^\//, ''),
            Button: undefined,
        }));
}
